import { XCircleIcon } from '@heroicons/react/24/solid';
import { Component, ErrorInfo, ReactNode, Suspense } from 'react';

import { Loader } from '@/components/Loader';
import {
  OrganizationSettingsCards,
  SettingContentCard,
  SettingSectionHeader,
} from '@/sections/settings/components';

/**
 * ErrorBoundary component for catching and displaying React errors.
 * Used to wrap Suspense boundaries to provide graceful error handling.
 *
 * Note: Ideally this would be extracted to a shared component, but keeping
 * it local for now to match the pattern in index.tsx.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('OrganizationTab error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

/**
 * OrganizationTab manages organization-level settings including profile,
 * SSO configuration, and Praetorian-specific features.
 *
 * Sections:
 * 1. Organization Profile - Name, logo, SSO providers
 *
 * Note: Notification settings have been moved to a dedicated Notification Settings tab.
 *
 * Architecture follows ScanSettingsTab pattern:
 * - Simple tab wrapper
 * - Complex logic in child cards components
 *
 * React 19 Features:
 * - useOptimistic: Delegated to child components
 * - Suspense: Progressive loading for async data
 * - ErrorBoundary: Graceful error handling for API failures
 */
export function OrganizationTab() {
  return (
    <>
      {/* Section 1: Organization Profile & SSO */}
      <div className="flex flex-col gap-6">
        <SettingSectionHeader
          title="Organization Profile"
          description="Manage organization profile, SSO providers, and advanced features."
        />

        <SettingContentCard data-testid="organization-settings-card">
          <ErrorBoundary
            fallback={
              <div className="flex h-full flex-col items-center justify-center gap-4 py-12">
                <XCircleIcon className="text-chariot-status-critical size-12" />
                <div className="flex flex-col gap-2 text-center">
                  <h2 className="text-chariot-text-primary text-lg font-bold">
                    Failed to Load Organization Settings
                  </h2>
                  <p className="text-chariot-text-secondary max-w-md text-sm">
                    Unable to retrieve organization settings. Please refresh the
                    page or contact support.
                  </p>
                </div>
              </div>
            }
          >
            <Suspense
              fallback={
                <div className="flex h-full flex-col items-center justify-center gap-4 py-12">
                  <Loader type="spinner" isLoading />
                  <p className="text-chariot-text-secondary text-sm">
                    Loading organization settings...
                  </p>
                </div>
              }
            >
              <OrganizationSettingsCards />
            </Suspense>
          </ErrorBoundary>
        </SettingContentCard>
      </div>
    </>
  );
}
